import {differenceInHours} from "date-fns";
import { Maybe, OrgMembership } from "apps/web/src/types";
import { Role } from "apps/web/src/types";
import { AthleteTeam } from "apps/web/src/types";

export type AthleteMobileState = "inactive" | "waiting" | "expired" | "active";

interface AthleteWithMobileState {
  activationLinkSent: Maybe<string>;
  orgMembership: Maybe<Pick<OrgMembership, "id" | "isActive">>;
}

export const getAthleteMobileState = (athlete: AthleteWithMobileState): AthleteMobileState => {
  if (athlete.orgMembership?.isActive) {
    return "active";
  } else {
    if (!athlete.activationLinkSent) {
      return "inactive";
    } else {
      if (differenceInHours(new Date(), new Date(athlete.activationLinkSent)) > 72) {
        return "expired";
      } else {
        return "waiting";
      }
    }
  }
};

export const NEW_ATHLETE_ROLES: Role[] = ["athlete"];

/**
 * Returns athlete phone #, preferring orgMembership.primaryPhone over athlete.phoneNumber if
 * Teamworks User Management is enabled.
 */
export const getDisplayPhoneNumber = (
  athlete: { orgMembership?: unknown; phoneNumber?: string | null } | null | undefined,
  isTeamworksUserManagementEnabled: boolean = false
): string | null => {
  if (!athlete) return null;
  const primaryPhone = (athlete.orgMembership as { primaryPhone?: string | null } | undefined)?.primaryPhone;
  return isTeamworksUserManagementEnabled ? primaryPhone ?? null : (athlete.phoneNumber ?? null);
};

/**
 * Returns athlete position string for a given team, or null (so caller can handle "empty" display) if no position is found.
 * Defers type of athleteTeams parameter to the implementation to allow for different types of gql fragments being passed in.
 * Both TUM flag status and legacy position fallback are handled server-side.
 */
export const getTeamAthletePositionForDisplay = (teamId: string, athleteTeams: unknown): string | null => {
    const positions = (athleteTeams as AthleteTeam[])?.find(t => t.team.id === teamId)?.positions;
    return positions?.length ? positions.map(p => p.name).join(", ") : null;
};

/**
 * Returns jersey number for a given team, falling back to the legacy athlete.jerseyNumber if the
 * per-team value is null (i.e. TUM not yet synced or org not TUM-enabled).
 * Defers type of athleteTeams parameter to the implementation to allow for different types of gql fragments being passed in.
 */
export const getTeamAthleteJerseyNumberForDisplay = (teamId: string, athleteTeams: unknown, fallback?: string | null): string | null => {
    const jerseyNumber = (athleteTeams as AthleteTeam[])?.find(t => t.team.id === teamId)?.jerseyNumber;
    return jerseyNumber ?? fallback ?? null;
};
